/**
 * Hover-карточка подробностей для строк боевой панели (заклинания, способности).
 *
 * Строки HUD нарочно однострочные — вся мета (время, дистанция, компоненты,
 * длительность, описание) живёт в этой карточке при наведении. Рендерится
 * через Radix Popover (порталится → не режется overflow-скроллом панели,
 * collision-flip у краёв экрана бесплатно). Карточка не интерактивна
 * (pointer-events: none) — не крадёт клик каста и фокус.
 */
import { useRef, useState, type ReactNode } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import type { HudSpell, HudFeature } from '../../lib/character-hud-store';
import { useI18n } from '../../i18n';

const HOVER_DELAY_MS = 250;

/** Таймер-стейт наведения: одна открытая карточка на список. */
export function useHoverDetails(): {
  hoveredId: string | null;
  hoverProps: (id: string) => { onMouseEnter: () => void; onMouseLeave: () => void };
  /** Force-close (and cancel the pending open). A row that becomes `disabled`
   *  under the cursor never fires mouseleave — e.g. casting the last slot —
   *  so the card would stick open until the next hover elsewhere. */
  clearHover: () => void;
} {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearHover = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setHoveredId(null);
  };
  const hoverProps = (id: string) => ({
    onMouseEnter: () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setHoveredId(id), HOVER_DELAY_MS);
    },
    onMouseLeave: clearHover,
  });
  return { hoveredId, hoverProps, clearHover };
}

export function HoverDetails({
  open,
  content,
  children,
}: {
  open: boolean;
  content: ReactNode;
  children: ReactNode;
}) {
  return (
    <Popover open={open}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      {open && (
        <PopoverContent
          side="left"
          sideOffset={10}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="w-72 p-3 rounded-lg border shadow-lg"
          style={{
            pointerEvents: 'none',
            backgroundColor: '#1B1714',
            borderColor: 'var(--paper-gold)',
            color: '#E8DCC8',
          }}
          data-testid="hover-details-card"
        >
          {content}
        </PopoverContent>
      )}
    </Popover>
  );
}

/* ───── content builders ───── */

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span style={{ color: 'rgba(232, 220, 200, 0.5)', fontFamily: 'JetBrains Mono, monospace', fontSize: 10 }}>
        {label}
      </span>
      <span style={{ textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function DescriptionBlock({ text }: { text: string }) {
  return (
    <div
      className="text-xs mt-2"
      style={{
        color: 'rgba(232, 220, 200, 0.85)',
        lineHeight: 1.45,
        maxHeight: 180,
        overflow: 'hidden',
        whiteSpace: 'pre-line',
      }}
    >
      {text}
    </div>
  );
}

export function SpellHoverContent({ spell }: { spell: HudSpell }) {
  const { t } = useI18n();
  const comps = spell.components
    ? (['v', 's', 'm'] as const).filter((k) => spell.components?.[k]).map((k) => k.toUpperCase()).join(' · ')
    : null;
  return (
    <div>
      <div className="text-sm font-medium" style={{ color: 'var(--paper-gold)', fontFamily: 'IBM Plex Sans, sans-serif' }}>
        {spell.name}
      </div>
      <div className="text-xs mb-2" style={{ color: 'rgba(232, 220, 200, 0.5)' }}>
        {spell.level === 0 ? t('characterHud.detailCantrip') : `${spell.level} ${t('characterHud.detailCircle')}`}
        {spell.school ? ` · ${spell.school}` : ''}
        {spell.concentration ? ` · ${t('characterHud.detailConcentration')}` : ''}
        {spell.ritual ? ` · ${t('characterHud.detailRitual')}` : ''}
      </div>
      <div className="flex flex-col gap-0.5">
        {spell.castingTime && <MetaRow label={t('characterHud.detailCastingTime')} value={spell.castingTime} />}
        {spell.range && <MetaRow label={t('characterHud.detailRange')} value={spell.range} />}
        {spell.duration && <MetaRow label={t('characterHud.detailDuration')} value={spell.duration} />}
        {comps && (
          <MetaRow
            label={t('characterHud.detailComponents')}
            value={comps + (spell.components?.materialDesc ? ` (${spell.components.materialDesc})` : '')}
          />
        )}
      </div>
      {spell.description && <DescriptionBlock text={spell.description} />}
      {spell.higherLevels && (
        <div className="text-xs mt-2" style={{ color: 'rgba(232, 220, 200, 0.6)', fontStyle: 'italic' }}>
          {t('characterHud.detailHigherLevels')}: {spell.higherLevels}
        </div>
      )}
    </div>
  );
}

export function FeatureHoverContent({ feature }: { feature: HudFeature }) {
  const { t } = useI18n();
  return (
    <div>
      <div className="text-sm font-medium" style={{ color: 'var(--paper-gold)', fontFamily: 'IBM Plex Sans, sans-serif' }}>
        {feature.name}
      </div>
      <div className="text-xs mb-2" style={{ color: 'rgba(232, 220, 200, 0.5)' }}>
        {feature.source}
        {feature.uses && feature.uses.max > 0
          ? ` · ${t('characterHud.detailUses')}: ${feature.uses.current}/${feature.uses.max}${feature.uses.recharge ? ` (${feature.uses.recharge})` : ''}`
          : ''}
      </div>
      {feature.description && <DescriptionBlock text={feature.description} />}
    </div>
  );
}
